import { useFetchUpgradePlansQuery } from "api/userApi";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Plan } from "types/api/user";
import { isInIframe } from "utils/proxyUtils";
import { getUser } from "../store/userSlice";

export const useLockFeature = ({ rank = 2, }: { rank: number }) => {
    const _dispatch = useDispatch();
    const user = useSelector(getUser);
    const { data: upgradePlans, isLoading: isFetchingUpgradePlans } =
        useFetchUpgradePlansQuery();

    const pricingPlans = useMemo(() => {
        if (!upgradePlans) return [];
        return upgradePlans.result.slice().sort((a, b) => a.rank - b.rank);
    }, [upgradePlans]);

    const currentPlan = useMemo(
        () => pricingPlans.find((p) => p.rank === rank),
        [pricingPlans, rank]
    );
    // Active plan determination
    const activePlan = currentPlan || pricingPlans[0];
    const userCycle = user.raw?.plan?.cycle ?? 'Monthly';
    const userCount = user?.raw?.plan?.users ?? 1;

    const calculateMaxUsers = (_card: Plan) => {
        if (!activePlan) return 1;
        const maxUsersMap = {
            Yearly: activePlan.max_users_yearly,
            Monthly: activePlan.max_users_monthly,
            Biyearly: activePlan.max_users_biyearly,
        } as { [key: string]: number };
        return maxUsersMap[userCycle];
    };

    const calculateBasePrice = (_card: Plan) => {
        if (!activePlan) return 0;
        const basePriceMap = {
            Yearly: Number(activePlan?.base_price_yearly) / 12,
            Monthly: Number(activePlan?.base_price_monthly),
            Biyearly: Number(activePlan?.base_price_biyearly),
        } as { [key: string]: number };
        return basePriceMap[userCycle];
    };

    const calculateBaseTeamSeatCost = () => {
        if (!activePlan || !activePlan.plans || !Array.isArray(activePlan.plans) || activePlan.plans.length < 3) {
            return "0";
        }
        const baseTeamCostMap = {
            Yearly: activePlan.plans[0]?.extra_seat_info_yearly,
            Monthly: activePlan.plans[1]?.extra_seat_info_monthly,
            Biyearly: activePlan.plans[2]?.extra_seat_info_biyearly,
        } as { [key: string]: string };
        return baseTeamCostMap[userCycle];
    };

    const calculatePrice = () => {
        if (!activePlan || !activePlan.plans) return 0;
        const maxUsers = calculateMaxUsers(activePlan);
        const basePrice = Number(calculateBasePrice(activePlan));
        const effectiveUsers = userCount <= maxUsers ? userCount - 1 : maxUsers - 1;

        const userCosts = {
            Yearly: (effectiveUsers * activePlan.plans[0]?.extra_seat_info_yearly) / 12,
            Monthly: effectiveUsers * activePlan.plans[1]?.extra_seat_info_monthly,
            Biyearly: effectiveUsers * activePlan.plans[2]?.extra_seat_info_biyearly,
        } as { [key: string]: number };

        const totalCost = userCosts[userCycle] + Math.round(basePrice);
        return totalCost;
    };
    const returnPlanPeriodValue = (monthly: unknown, yearly: unknown, biYearly: unknown) => {
        const periodMap = {
            Yearly: yearly,
            Monthly: monthly,
            Biyearly: biYearly,
        } as never;
        return periodMap[userCycle];
    };

    const handleSubscribe = ({ user = userCount }: { user?: number }) => {
        const baseUrl = `${import.meta.env.VITE_APP_ATARIM_URL}`;
        const appendQueryParams = (url: string, params: string) => {
            return url.includes('?') ? `${url}&${params}` : `${url}?${params}`;
        };
        try {
            if (isInIframe()) {
                // Send message to parent for navigation
                window.parent.postMessage({
                    type: 'NAVIGATE_BILLING',
                    url: `&upgrade=true&plan=${activePlan.name}&users=${user}&billed=${userCycle}`,
                }, '*'); // Will likely replace '*' with production/test origins
            } else {
                window.open(appendQueryParams(baseUrl, `upgrade=true&plan=${activePlan.name}&users=${user}&billed=${userCycle}`), '_blank');
            }
        } catch (error) {
            console.error('Error redirecting to upgrade URL:', error);
            // Fallback to direct navigation if postMessage fails
            window.open(appendQueryParams(baseUrl, `upgrade=true&plan=${activePlan.name}&users=${user}&billed=${userCycle}`), '_blank');
        }
    }

    const handleComparePlans = () => {
        const baseUrl = `${import.meta.env.VITE_APP_ATARIM_URL}`;

        try {
            if (isInIframe()) {
                // Send message to parent for navigation
                window.parent.postMessage({
                    type: 'NAVIGATE_BILLING',
                    url: `&upgrade=true`,
                }, '*'); // Will likely replace '*' with production/test origins
            } else {
                // Open a new window with the upgrade parameter
                window.open(`${baseUrl}?upgrade=true`, '_blank');
            }
        } catch (error) {
            console.error('Error redirecting to upgrade URL:', error);
            // Fallback to direct navigation if postMessage fails
            // Open a new window with the upgrade parameter
            window.open(`${baseUrl}?upgrade=true`, '_blank');

        }

    }

    return {
        pricingPlans,
        activePlan,
        isFetchingUpgradePlans,
        calculateBasePrice,
        calculateBaseTeamSeatCost,
        returnPlanPeriodValue,
        calculatePrice,
        handleSubscribe,
        handleComparePlans,
    };
};